import {
  Box, Button, Form, FormField, Link, SidebarModal,
} from '@domain-group/suit-theme'

import type { SidebarModalProps } from '@domain-group/suit-theme'

import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArchive,
  faDownload,
  faThumbsDown,
  faThumbsUp,
} from '@fortawesome/pro-light-svg-icons'

import type { TemplatePageModel } from '@models/types'

import useTemplatePage, { useTemplatePageForm } from '@hooks/useTemplatePage'
import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import AuthenticatedDownload from '@components/AuthenticatedDownload'

const MODAL_KEY = 'EditTemplateModal'

type EditTemplateModalProps = {
  callbacks: {
    editTemplatePage: any,
  },
  templatePage: TemplatePageModel,
  showModal: boolean,
}

const confirmToggleProductionReady = (productionReady: boolean | undefined, toggleProductionReadyFn: () => void) => {
  const toggleProductionReadyPayload: HandleCallbackActionParams = {
    callbacks: {
      // @ts-expect-error TS2322
      action: toggleProductionReadyFn,
    },
    toastText: productionReady ? 'Template Unapproved' : 'Template Approved',
  }

  swal
    .fire({
      title: productionReady ? 'Unapprove Template?' : 'Approve Template?',
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, ${productionReady ? 'unapprove' : 'approve'} it.`,
      confirmButtonColor: productionReady ? '#DD6B55' : '#43ac6a',
    })
    .then(({ value }) => {
      if (value){
        handleCallbackAction(toggleProductionReadyPayload)
      }
    })
}

const EditTemplateModal = (props: SidebarModalProps & EditTemplateModalProps) => {
  const {
    templatePage,
    showModal,
    callbacks,
    callbacks: { editTemplatePage, closeModal },
  } = props

  const templatePagePayload = useTemplatePage(templatePage)
  const {
    callbacks: {
      deleteTemplatePage,
      updateTemplatePage: updateFn,
      toggleProductionReady,
    },
    urls: {
      templatePageDownloadPdfUrl,
    },
    templatePage: {
      production_ready,
    },
  } = templatePagePayload

  const formPayload = useTemplatePageForm(templatePage, {
    validateOn: 'change',
  })

  const {
    entityState,
    entityState: { title },
    handlers,
    saveEnabled,
  } = formPayload

  const editTemplatePagePayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Template Page Updated',
  }

  const handleSave = async () => {
    editTemplatePage(editTemplatePagePayload)
  }

  const confirmDelete = (deleteFn: () => void) => {
    const deleteTemplatePagePayload: HandleCallbackActionParams = {
      callbacks: {
        // @ts-expect-error TS2322
        action: deleteFn,
        afterAction: closeModal,
      },
      toastText: 'Template Archived',
    }

    swal
      .fire({
        title: 'Archive Template?',
        text: 'Are you sure?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, archive it.',
        confirmButtonColor: '#DD6B55',
      })
      .then(({ value }) => {
        if (value){
          handleCallbackAction(deleteTemplatePagePayload)
        }
      })
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Manage" titleSecondLine="Template" />

      <SidebarModal.Content>
        <Box flexDirection="column">
          <FormField direction="column" label="* Template Title" marginTop="large">
            <input name="title" type="text" value={title} {...handlers} />
          </FormField>

          <Form.Field label="More Options" marginTop="large">
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faArchive} />}
              onClick={() => confirmDelete(deleteTemplatePage)}
              // @ts-expect-error TS2322
              marginBottom="small"
              size="medium"
            >
              Archive
            </Button>

            <AuthenticatedDownload key="download-template">
              <Link href={templatePageDownloadPdfUrl}>
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faDownload} />}
                  // @ts-expect-error TS2322
                  marginBottom="small"
                  size="medium"
                >
                  Download Template
                </Button>
              </Link>
            </AuthenticatedDownload>

            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={production_ready ? faThumbsDown : faThumbsUp} />}
              onClick={() => confirmToggleProductionReady(production_ready, toggleProductionReady)}
              // @ts-expect-error TS2322
              marginBottom="small"
              size="medium"
            >
              {production_ready ? 'Unapprove' : 'Approve for Production'}
            </Button>
          </Form.Field>
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loadingBubbleColor="white"
          onClick={handleSave}
          size="large"
        >
          Save
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: SidebarModalProps & EditTemplateModalProps) => (
  // @ts-expect-error TS2786
  <SidebarModal.RenderController {...props}>
    <EditTemplateModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
